import React from 'react';
import { Link, WarningIcon } from '@backstage/core-components';
import { Tooltip, Chip, Box } from '@material-ui/core';
import { VersionInfo, ServiceRow, formatDate, shouldShowWarning, getWarningTooltip, isRecent, getChangeLogFileName } from '../utils';
import { SummaryFilter, CustomDateRange } from '../hooks/useFilters';
import { CombinedReleaseHistory } from '@internal/backstage-plugin-ewii-azure';
import { ChangeLogModal } from './ChangeLogModal';

interface VersionCellProps {
  cell: VersionInfo;
  env: string;
  row: ServiceRow;
  isAcceptanceFilterActive?: boolean;
  isProductionFilterActive?: boolean;
  isStagedFilterActive?: boolean;
  summaryFilter?: SummaryFilter | null;
  customDateRange?: CustomDateRange | null;
  releaseHistory?: Record<string, CombinedReleaseHistory[]>;
}

export const VersionCell: React.FC<VersionCellProps> = ({
  cell,
  env,
  row,
  isAcceptanceFilterActive = false,
  isProductionFilterActive = false,
  isStagedFilterActive = false,
  summaryFilter = null,
  customDateRange = null,
  releaseHistory = {},
}) => {
  const compareCell = env === 'production' ? row['acceptance'] : env === 'acceptance' ? row['test'] : undefined;
  const showWarning = compareCell ? shouldShowWarning(cell, compareCell) : false;
  const formattedDate = cell.buildDate ? formatDate(cell.buildDate) : '';
  const isUnhealthy = cell.healthStatus?.some(status => status.status === 'Unhealthy') ?? false;
  const isAcceptanceWarning =
    typeof row['acceptance'] === 'object' && row['acceptance'] &&
    typeof row['test'] === 'object' && row['test'] &&
    shouldShowWarning(row['acceptance'], row['test']);
  const isProductionWarning =
    typeof row['production'] === 'object' && row['production'] &&
    typeof row['acceptance'] === 'object' && row['acceptance'] &&
    shouldShowWarning(row['production'], row['acceptance']);

  // Check if this service was deployed in the custom date range using release history
  const isInCustomDateRange = customDateRange && customDateRange.from && customDateRange.to && cell.componentName ? (() => {
    const customReleases = releaseHistory[`${env}_custom`] || [];
    return customReleases.some(release => release.component === cell.componentName);
  })() : false;

  // Check for staged releases in production
  const stagedRelease = env === 'production' && cell.componentName ? (() => {
    const productionReleases = releaseHistory['production_30d'] || [];
    return productionReleases.find(release => 
      release.component === cell.componentName && 
      release.status === 'staged'
    );
  })() : null;

  const hasStaged = Boolean(stagedRelease);

  let cellStyle = {};
  
  // Check custom date range filter first (only when both dates are filled)
  if (customDateRange && customDateRange.from && customDateRange.to && !isInCustomDateRange) {
    cellStyle = { opacity: 0.3 };
  }
  // Check summary filter
  else if (summaryFilter) {
    if (summaryFilter.type === 'recent') {
      const isRecentFunction = summaryFilter.timePeriod === '7d' ? 
        (date?: string) => isRecent(date, 7 * 24) : 
        summaryFilter.timePeriod === '30d' ? 
        (date?: string) => isRecent(date, 30 * 24) : isRecent;
      if (summaryFilter.environment) {
        // Filter by specific environment
        if (env !== summaryFilter.environment || !isRecentFunction(cell.buildDate)) {
          cellStyle = { opacity: 0.3 };
        }
      } else {
        // Filter by any environment
        if (!isRecentFunction(cell.buildDate)) {
          cellStyle = { opacity: 0.3 };
        }
      }
    } else if (summaryFilter.type === 'health') {
      if (summaryFilter.environment) {
        // Filter by specific environment
        if (env !== summaryFilter.environment || !isUnhealthy) {
          cellStyle = { opacity: 0.3 };
        }
      } else {
        // Filter by any environment
        if (!isUnhealthy) {
          cellStyle = { opacity: 0.3 };
        }
      }
    }
  } else if (isAcceptanceFilterActive) {
    if (!(isAcceptanceWarning && (env === 'acceptance' || env === 'test'))) {
      cellStyle = { opacity: 0.3 };
    }
  } else if (isProductionFilterActive) {
    if (!(isProductionWarning && (env === 'production' || env === 'acceptance'))) {
      cellStyle = { opacity: 0.3 };
    }
  } else if (isStagedFilterActive && !hasStaged) {
    cellStyle = { opacity: 0.3 };
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start', justifyContent: 'flex-start', minHeight: '100%', ...cellStyle }}>
      <div>
        <Link to={cell.hasReleaseHistory ?
            `/catalog/default/component/${cell.componentName}/release-history` :
            `/catalog/default/component/${cell.componentName}`}>
          {cell.componentName}
        </Link>
      </div>
      <div style={{ height: '8px' }} />
      {cell.healthStatus && cell.healthStatus.length > 0 && (
        <div style={{ color: isUnhealthy ? 'red' : 'green' }}>
            <span style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {isUnhealthy ? 'Unhealthy' : 'Healthy'}
            {isUnhealthy && (
              <Tooltip title="One or more health checks failed" arrow>
              <span style={{ color: 'red', display: 'flex', alignItems: 'center', marginLeft: 4 }}>
                <WarningIcon fontSize="small" />
              </span>
              </Tooltip>
            )}
            </span>
        </div>
      )}
      
      {/* Staged Release Notification */}
      {stagedRelease && (
        <Box mt={1} mb={1}>
          <Chip
            label="Staged Release Ready"
            size="small"
            style={{
              backgroundColor: '#fff3cd',
              color: '#856404',
              border: '1px solid #ffc107',
              marginBottom: '8px'
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Link to={stagedRelease.releaseUrl} target="_blank" rel="noopener noreferrer">
                <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
                  View Build ({stagedRelease.releaseNumber})
                </span>
              </Link>
                             {(() => {
                 const fileName = getChangeLogFileName(stagedRelease.changeLogFile, stagedRelease.changeLogUrl);
                 return fileName ? (
                   <ChangeLogModal 
                     component={cell.componentName} 
                     fileName={fileName}
                     displayText="View Changes"
                     fullRowExpansion={true}
                   />
                 ) : null;
               })()}
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {stagedRelease.releaseDate ? formatDate(stagedRelease.releaseDate.toISOString()) : 'N/A'}
            </div>
          </div>
        </Box>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontWeight: 'bold' }}>Release version</span>
          {showWarning && (
            <Tooltip title={getWarningTooltip(env, cell, compareCell)} arrow>
              <span style={{ color: env === 'production' ? 'orange' : 'lightblue', display: 'flex', alignItems: 'center' }}>
                <WarningIcon fontSize="small" />
              </span>
            </Tooltip>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {cell.branch ? (
              <span style={{ fontFamily: 'monospace' }}>
                {cell.version} ({cell.branch || 'N/A'})
              </span>
            ) : (
              <span>{cell.version || 'N/A'}</span>
            )}
          </div>
          {cell.commit && (
            <span style={{ fontFamily: 'monospace' }}>{cell.commit}</span>
          )}
        </div>
      </div>
      {formattedDate && (
        <>
          <div style={{ height: '8px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 'bold' }}>Release date</span>
            <span>{formattedDate}</span>
          </div>
        </>
      )}
      {(() => {
        const fileName = getChangeLogFileName(cell.changeLogFile, cell.changeLogUrl);
        return fileName ? (
          <div style={{ marginTop: '8px' }}>
            <ChangeLogModal 
              component={cell.componentName} 
              fileName={fileName}
              displayText="View changelog"
              fullRowExpansion={true}
            />
          </div>
        ) : null;
      })()}
    </div>
  );
}; 